import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import api from 'state/axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Close, SmartToy } from '@mui/icons-material';
// import { useTheme } from '@mui/material/styles';
import { decrementUnread } from 'state';
import { getSocket } from 'state/socket';
import ChatMessage from './ChatMessage';
import ChatMessageInput from './ChatMessageInput';


const ChatWindow = ({ 
  conversation, 
  currentUserId, 
  onBack, 
  onClose 
}) => {
  const dispatch = useDispatch();
  // const theme = useTheme();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [aiThinking, setAIThinking] = useState(false);
  const bottomRef = useRef(null);
  const typingTimerRef = useRef({});

  
  const conversationId = conversation._id;

  const getConversationName = () => {
    if (conversation.type === 'ai') return 'DigitAir AI';
    if (conversation.type === 'group') return conversation.name;
    const other = conversation.participants?.find((p) => String(p._id) !== String(currentUserId));
    return other ? `${other.firstName} ${other.lastName}` : 'Chat';
  };

  const getConversationAvatar = () => {
    if (conversation.type === 'group') return conversation.avatar || undefined;
    const other = conversation.participants?.find((p) => String(p._id) !== String(currentUserId));
    return other?.picturePath || undefined;
  };

  /* Fetch messages + mark as read */
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/messages/${conversationId}`);
        setMessages(res.data);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();

    api.patch(`/messages/${conversationId}/read`, {}).then(() => {
      dispatch(decrementUnread());
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  /* Socket: join/leave room + listen for messages + typing */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    socket.emit('chat:join', conversationId);

    const handleMessage = (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      // Auto mark as read since window is open
      api.patch(`/messages/${conversationId}/read`, {});
    };

    const handleDeleted = ({ messageId }) => {
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    };

    const handleTyping = ({ userId }) => {
      if (String(userId) === String(currentUserId)) return;
      setTypingUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
      clearTimeout(typingTimerRef.current[userId]);
      typingTimerRef.current[userId] = setTimeout(() => {
        setTypingUsers((prev) => prev.filter((id) => id !== userId));
      }, 2500);
    };

    const handleAIThinking = ({ conversationId: cid }) => {
      if (String(cid) === String(conversationId)) setAIThinking(true);
    };
    const handleAIDone = ({ conversationId: cid }) => {
      if (String(cid) === String(conversationId)) setAIThinking(false);
    };

    socket.on('chat:message', handleMessage);
    socket.on('chat:messageDeleted', handleDeleted);
    socket.on('chat:typing', handleTyping);
    socket.on('chat:ai-thinking', handleAIThinking);
    socket.on('chat:ai-done', handleAIDone);

    return () => {
      socket.emit('chat:leave', conversationId);
      socket.off('chat:message', handleMessage);
      socket.off('chat:messageDeleted', handleDeleted);
      socket.off('chat:typing', handleTyping);
      socket.off('chat:ai-thinking', handleAIThinking);
      socket.off('chat:ai-done', handleAIDone);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, currentUserId]);

  /* Scroll to bottom on new messages */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback((newMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m._id === newMessage._id)) return prev;
      return [...prev, newMessage];
    });
  }, []);

  const handleDelete = useCallback(async (messageId) => {
    try {
      await api.delete(`/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    } catch (err) {
      toast.error(err.message || "Failed to delete!");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const name = getConversationName();
  const avatarSrc = getConversationAvatar();
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();


  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      height="100%"
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {onBack && (
          <IconButton size="small" onClick={onBack}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        {conversation.type === 'ai' ? (
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <SmartToy sx={{ fontSize: 16 }} />
          </Avatar>
        ) : (
          <Avatar src={avatarSrc} sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
            {initials}
          </Avatar>
        )}
        <Box flex={1}>
          <Typography variant="body2" fontWeight={600}>
            {name}
          </Typography>
          {aiThinking && (
            <Typography variant="caption" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={10} /> AI is thinking...
            </Typography>
          )}
          {!aiThinking && typingUsers.length > 0 && (
            <Typography variant="caption" color="primary.main">
              typing…
            </Typography>
          )}
        </Box>
        {onClose && (
          <Tooltip title="Close">
            <IconButton size="medium" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" pt={4}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Box display="flex" justifyContent="center" pt={4}>
            <Typography variant="caption" color="text.disabled">
              No messages yet. Say hello!
            </Typography>
          </Box>
        ) : (
          messages?.map((msg) => (
            <ChatMessage
              key={msg._id}
              message={msg}
              isOwn={String(msg.sender?._id ?? msg.sender) === String(currentUserId)}
              canDelete={true}
              onDelete={handleDelete}
            />
          ))
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Input */}
      <ChatMessageInput 
        conversationId={conversationId} 
        onSend={handleSend} 
      />
    </Box>
  );
};

export default ChatWindow;
